import { camelize } from "humps";
import findIndex from "lodash/findIndex";
import map from "lodash/map";
import orderBy from "lodash/orderBy";
import { useActivityShowContext } from "PFApp/activities/show/context/activity_show_context";
import { useCurrentActivity } from "PFApp/activities/show/hooks/use_current_activity";
import { useMatchSidebarCustomTypes } from "PFApp/activities/show/hooks/use_match_sidebar_info_custom_types";
import { MatchesShortlistColumnId } from "PFApp/activities/types/matches_shortlist_column_id";
import { getRelatedSkills } from "PFApp/hooks/matches";
import { ColumnHelper } from "PFComponents/table/table.types";
import { useCustomValueToString } from "PFCore/helpers/custom_value";
import { getMainPosition } from "PFCore/helpers/profile";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import {
  MatchesShortlistTableEntry,
  SKILLS_IMPORTANCE_COLUMNS_DATA,
  TABLE_SIZES
} from "../activity_tabs.utils";
import {
  CustomValueCell,
  RoleProfileActionsCell,
  ScoreValueCell,
  SkillsCell,
  WorkforceMemberCell
} from "../components/table_cells";
import { SkillsOfImportanceCell } from "../components/table_cells/skills_of_importance_cell";
import { TableHeader } from "../components/table_header";

type UseMatchesShortlistTableColumns = {
  isShortlistTabActive: boolean;
};

export const useMatchesShortlistTableColumns = <TableEntry extends MatchesShortlistTableEntry>({
  isShortlistTabActive
}: UseMatchesShortlistTableColumns) => {
  const { t } = useTranslation("activities", { keyPrefix: "show.matches.table" });
  const activity = useCurrentActivity();
  const { selectedProfilesListColumns, isFillDisabled } = useActivityShowContext();
  const { getCustomValuesListString } = useCustomValueToString();

  const { matchSidebarInfoCustomTypes } = useMatchSidebarCustomTypes();

  const generateColumns = useCallback(
    (columnHelper: ColumnHelper<TableEntry>) => {
      const columns = [
        columnHelper.accessor((row) => row.match?.scores.normalizedScore, {
          id: MatchesShortlistColumnId.Match,
          header: () => <TableHeader title={t("match")} />,
          cell: (info) => <ScoreValueCell score={info.getValue()} />,
          size: TABLE_SIZES.match,
          enableSorting: false
        }),
        columnHelper.accessor((row) => row.match?.scores.availabilityScore, {
          id: MatchesShortlistColumnId.Availability,
          header: () => <TableHeader title={t("availabilityAbbreviation")} />,
          cell: (info) => <ScoreValueCell score={info.getValue()} />,
          size: TABLE_SIZES.availability,
          enableSorting: false
        }),
        columnHelper.accessor((row) => row, {
          id: MatchesShortlistColumnId.WorkforceMember,
          header: () => <TableHeader title={t("workforceMember")} />,
          cell: (info) => {
            const { shortlist, match } = info.getValue();

            return <WorkforceMemberCell profile={match?.profile ?? shortlist!.profile} />;
          },
          // no size set so column will take remaining space
          enableSorting: false
        }),
        ...SKILLS_IMPORTANCE_COLUMNS_DATA.map(({ id, importance }) =>
          columnHelper.accessor((row) => row, {
            id,
            header: () => <TableHeader title={t(id)} />,
            cell: (info) => {
              const { match, shortlist } = info.getValue();

              return <SkillsOfImportanceCell importance={importance} match={match} shortlist={shortlist} />;
            },
            size: TABLE_SIZES.skills,
            enableSorting: false
          })
        ),
        columnHelper.accessor((row) => row, {
          id: MatchesShortlistColumnId.RelatedSkills,
          header: () => <TableHeader title={t("relatedSkills")} />,
          cell: (info) => {
            const { match, shortlist } = info.getValue();
            const { matchSkills, roleSkills } = getRelatedSkills(
              match?.customValues || { direct: [], indirect: [], related: [], parent: [], child: [] }
            );
            const profileId = match?.profile.id ?? shortlist!.profile.id;

            return <SkillsCell profileId={profileId} matchSkills={matchSkills} roleSkills={roleSkills} />;
          },
          size: TABLE_SIZES.skills,
          enableSorting: false
        }),
        ...matchSidebarInfoCustomTypes.map((customType) =>
          columnHelper.accessor((row) => row, {
            id: customType.name,
            header: () => <TableHeader title={customType.display_as ?? customType.name} />,
            cell: (info) => {
              const { match, shortlist } = info.getValue();
              const profile = match?.profile ?? shortlist!.profile;

              const position = getMainPosition(profile);

              const customValues = profile[camelize(customType.name)] || position[camelize(customType.name)];
              const text = customValues
                ? getCustomValuesListString(customValues, customType.value_type)
                : undefined;

              return <CustomValueCell text={text} />;
            },
            size: TABLE_SIZES.customValue,
            enableSorting: false
          })
        ),
        columnHelper.accessor((row) => row, {
          id: MatchesShortlistColumnId.Actions,
          header: () => null,
          cell: (info) => {
            const { match, shortlist } = info.getValue();
            const profile = match?.profile ?? shortlist!.profile;

            return (
              <RoleProfileActionsCell
                role={activity}
                profile={profile}
                shortlist={shortlist ?? match!.shortlist}
                isFillDisabled={isFillDisabled}
                isShortlistTabActive={isShortlistTabActive}
                named={isShortlistTabActive}
              />
            );
          },
          size: TABLE_SIZES.actions,
          enableSorting: false
        })
      ];

      const selectedColumnNames = map(selectedProfilesListColumns, "name");

      return orderBy(
        columns.filter(({ id }) => selectedColumnNames.includes(id || "")),
        ({ id }) => findIndex(selectedColumnNames, (name) => id === name)
      );
    },

    [
      activity,
      getCustomValuesListString,
      isFillDisabled,
      isShortlistTabActive,
      matchSidebarInfoCustomTypes,
      selectedProfilesListColumns,
      t
    ]
  );

  return {
    generateColumns
  };
};
